import React, { useState, useEffect } from 'react';
import Navbar from '../components/Navbar';
import { getRules, createRule, deleteRule, getViolations, logViolation, extractErrorMessage } from '../services/api';
import { FaPlus, FaTrash, FaExclamationTriangle } from 'react-icons/fa';
import { useToast } from '../components/Toast';
import '../styles/Rules.css';

function Rules({ user }) {
  const toast = useToast();
  const [rules, setRules] = useState([]);
  const [violations, setViolations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('rules');
  const [showModal, setShowModal] = useState(false);
  const [showViolationModal, setShowViolationModal] = useState(false);
  const [savingRule, setSavingRule] = useState(false);
  const [formErrorRule, setFormErrorRule] = useState(null);
  const [savingViolation, setSavingViolation] = useState(false);
  const [formErrorViolation, setFormErrorViolation] = useState(null);
  const [newRule, setNewRule] = useState({
    category: 'general',
    rule_text: '',
    penalty_points: 0
  });
  const [newViolation, setNewViolation] = useState({
    user_id: '',
    rule_id: '',
    notes: ''
  });

  useEffect(() => {
    fetchRules();
    fetchViolations();
  }, []);

  const fetchRules = async () => {
    try {
      setLoading(true);
      const data = await getRules();
      setRules(data.rules);
    } catch (error) {
      console.error('Error fetching rules:', error);
      toast.error(extractErrorMessage(error, 'Regeln konnten nicht geladen werden'));
    } finally {
      setLoading(false);
    }
  };

  const fetchViolations = async () => {
    try {
      const data = await getViolations();
      setViolations(data.violations);
    } catch (error) {
      console.error('Error fetching violations:', error);
      toast.error(extractErrorMessage(error, 'Verstöße konnten nicht geladen werden'));
    }
  };

  const handleCreateRule = async (e) => {
    e.preventDefault();
    if (savingRule) return;
    setSavingRule(true);
    setFormErrorRule(null);
    try {
      await createRule(newRule);
      setNewRule({ category: 'general', rule_text: '', penalty_points: 0 });
      setShowModal(false);
      toast.success('Regel erstellt');
      fetchRules();
    } catch (error) {
      console.error('Error creating rule:', error);
      setFormErrorRule(extractErrorMessage(error, 'Fehler beim Erstellen der Regel'));
    } finally {
      setSavingRule(false);
    }
  };

  const handleDeleteRule = async (id) => {
    if (!window.confirm('Regel wirklich deaktivieren?')) return;
    try {
      await deleteRule(id);
      fetchRules();
    } catch (error) {
      console.error('Error deleting rule:', error);
    }
  };

  const handleLogViolation = async (e) => {
    e.preventDefault();
    if (savingViolation) return;
    setSavingViolation(true);
    setFormErrorViolation(null);
    try {
      await logViolation(newViolation);
      setNewViolation({ user_id: '', rule_id: '', notes: '' });
      setShowViolationModal(false);
      toast.success('Verstoß gemeldet');
      fetchViolations();
    } catch (error) {
      console.error('Error logging violation:', error);
      setFormErrorViolation(extractErrorMessage(error, 'Fehler beim Melden des Verstoßes'));
    } finally {
      setSavingViolation(false);
    }
  };

  const closeRuleModal = () => {
    if (savingRule) return;
    setShowModal(false);
    setFormErrorRule(null);
  };

  const closeViolationModal = () => {
    if (savingViolation) return;
    setShowViolationModal(false);
    setFormErrorViolation(null);
  };

  const getCategoryLabel = (category) => {
    const labels = {
      communication: 'Kommunikation',
      behavior: 'Verhalten',
      tasks: 'Aufgaben',
      general: 'Allgemein'
    };
    return labels[category] || category;
  };

  const groupedRules = rules.reduce((acc, rule) => {
    if (!acc[rule.category]) acc[rule.category] = [];
    acc[rule.category].push(rule);
    return acc;
  }, {});

  return (
    <div className="app-layout">
      <Navbar user={user} />
      <main className="main-content">
        <div className="page-header">
          <h1>Regeln & Verstöße</h1>
          {user.isAdmin && (
            <div className="header-actions">
              <button className="add-btn violation" onClick={() => setShowViolationModal(true)}>
                <FaExclamationTriangle /> Verstoß melden
              </button>
              <button className="add-btn" onClick={() => setShowModal(true)}>
                <FaPlus /> Neue Regel
              </button>
            </div>
          )}
        </div>

        <div className="tab-buttons">
          <button className={activeTab === 'rules' ? 'active' : ''} onClick={() => setActiveTab('rules')}>
            Regeln ({rules.length})
          </button>
          <button className={activeTab === 'violations' ? 'active' : ''} onClick={() => setActiveTab('violations')}>
            Verstöße ({violations.length})
          </button>
        </div>

        {loading ? (
          <div className="loading-container">
            <div className="spinner"></div>
          </div>
        ) : activeTab === 'rules' ? (
          <div className="rules-list">
            {Object.entries(groupedRules).map(([category, categoryRules]) => (
              <div key={category} className="rule-category">
                <h2 className="category-title">{getCategoryLabel(category)}</h2>
                <div className="rules-grid">
                  {categoryRules.map(rule => (
                    <div key={rule.id} className="rule-card">
                      <div className="rule-content">
                        <span className="rule-id">#{rule.id}</span>
                        <p className="rule-text">{rule.rule_text}</p>
                      </div>
                      <div className="rule-footer">
                        <span className="penalty">-{rule.penalty_points} Punkte</span>
                        {user.isAdmin && (
                          <button className="delete-btn" onClick={() => handleDeleteRule(rule.id)}>
                            <FaTrash />
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
            {rules.length === 0 && <div className="no-data">Keine Regeln vorhanden</div>}
          </div>
        ) : (
          <div className="violations-list">
            <table className="violations-table">
              <thead>
                <tr>
                  <th>Datum</th>
                  <th>Regel</th>
                  <th>Kategorie</th>
                  <th>Strafpunkte</th>
                  <th>Notizen</th>
                </tr>
              </thead>
              <tbody>
                {violations.length > 0 ? violations.map(v => (
                  <tr key={v.id}>
                    <td>{new Date(v.created_at).toLocaleString()}</td>
                    <td>{v.rule_text}</td>
                    <td>{getCategoryLabel(v.category)}</td>
                    <td className="penalty">-{v.penalty_points}</td>
                    <td>{v.notes || '-'}</td>
                  </tr>
                )) : (
                  <tr>
                    <td colSpan="5" className="no-data">Keine Verstöße vorhanden</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {showModal && (
          <div className="modal-overlay" onClick={closeRuleModal}>
            <div className="modal" onClick={e => e.stopPropagation()} role="dialog" aria-modal="true">
              <h2>Neue Regel</h2>
              {formErrorRule && <div className="shared-form-error-banner">{formErrorRule}</div>}
              <form onSubmit={handleCreateRule}>
                <div className="form-group">
                  <label>Kategorie</label>
                  <select
                    value={newRule.category}
                    onChange={e => setNewRule({...newRule, category: e.target.value})}
                  >
                    <option value="communication">Kommunikation</option>
                    <option value="behavior">Verhalten</option>
                    <option value="tasks">Aufgaben</option>
                    <option value="general">Allgemein</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Regeltext *</label>
                  <textarea
                    value={newRule.rule_text}
                    onChange={e => setNewRule({...newRule, rule_text: e.target.value})}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Strafpunkte</label>
                  <input
                    type="number"
                    value={newRule.penalty_points}
                    onChange={e => setNewRule({...newRule, penalty_points: parseInt(e.target.value) || 0})}
                  />
                </div>
                <div className="modal-actions">
                  <button type="button" className="cancel-btn" onClick={closeRuleModal} disabled={savingRule}>Abbrechen</button>
                  <button type="submit" className="submit-btn" disabled={savingRule}>{savingRule ? 'Speichert…' : 'Erstellen'}</button>
                </div>
              </form>
            </div>
          </div>
        )}

        {showViolationModal && (
          <div className="modal-overlay" onClick={closeViolationModal}>
            <div className="modal" onClick={e => e.stopPropagation()} role="dialog" aria-modal="true">
              <h2>Verstoß melden</h2>
              {formErrorViolation && <div className="shared-form-error-banner">{formErrorViolation}</div>}
              <form onSubmit={handleLogViolation}>
                <div className="form-group">
                  <label>Benutzer-ID *</label>
                  <input
                    type="text"
                    value={newViolation.user_id}
                    onChange={e => setNewViolation({...newViolation, user_id: e.target.value})}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Regel *</label>
                  <select
                    value={newViolation.rule_id}
                    onChange={e => setNewViolation({...newViolation, rule_id: e.target.value})}
                    required
                  >
                    <option value="">Regel auswählen...</option>
                    {rules.map(rule => (
                      <option key={rule.id} value={rule.id}>
                        #{rule.id} - {rule.rule_text.slice(0, 50)}... (-{rule.penalty_points} Pkt)
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Notizen</label>
                  <textarea
                    value={newViolation.notes}
                    onChange={e => setNewViolation({...newViolation, notes: e.target.value})}
                  />
                </div>
                <div className="modal-actions">
                  <button type="button" className="cancel-btn" onClick={closeViolationModal} disabled={savingViolation}>Abbrechen</button>
                  <button type="submit" className="submit-btn" disabled={savingViolation}>{savingViolation ? 'Speichert…' : 'Melden'}</button>
                </div>
              </form>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

export default Rules;
